import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { useAllocatedDedicatedClis } from '@/features/voice-campaigns/api';
import {
  useArchiveCampaignCli,
  useCampaignCliList,
  useEditCampaignCli,
} from '../api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: number;
  campaignName: string;
  onSaved?: () => void;
}

export function EditCampaignCliDialog({
  open,
  onOpenChange,
  campaignId,
  campaignName,
  onSaved,
}: Props) {
  const cliPool = useAllocatedDedicatedClis();
  const fetchCurrent = useCampaignCliList();
  const edit = useEditCampaignCli();
  const archive = useArchiveCampaignCli();

  /** CLIs currently bound to this campaign (loaded when dialog opens). */
  const [originalCliIds, setOriginalCliIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [archiveConfirm, setArchiveConfirm] = useState<string[] | null>(null);

  useEffect(() => {
    if (!open) {
      setOriginalCliIds([]);
      setSelected([]);
      setArchiveConfirm(null);
      return;
    }
    fetchCurrent.mutate(campaignId, {
      onSuccess: (rows) => {
        const ids = rows.map((r) => String(r.cliId));
        setOriginalCliIds(ids);
        setSelected(ids);
      },
      onError: () => toast.error('Could not load the campaign’s CLIs.'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaignId]);

  const cliOptions = useMemo(
    () =>
      (cliPool.data ?? []).map((c) => ({
        value: String(c.cliId),
        label: c.cli,
      })),
    [cliPool.data],
  );

  const cliMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of cliPool.data ?? []) m.set(String(c.cliId), c.cli);
    return m;
  }, [cliPool.data]);

  const removedClis = useMemo(
    () =>
      originalCliIds
        .filter((id) => !selected.includes(id))
        .map((id) => cliMap.get(id) ?? '')
        .filter(Boolean),
    [originalCliIds, selected, cliMap],
  );

  const handleSave = () => {
    if (selected.length === 0) {
      toast.error('Pick at least one CLI.');
      return;
    }
    // If any CLIs were removed, ask whether to archive them.
    if (removedClis.length > 0) {
      setArchiveConfirm(removedClis);
      return;
    }
    saveCli(false);
  };

  const saveCli = (archiveDropped: boolean) => {
    const cliList = selected.map((id) => ({
      cliId: Number(id),
      cli: cliMap.get(id) ?? '',
    }));
    edit.mutate(
      { campaignId, cliList },
      {
        onSuccess: (res) => {
          if (archiveDropped && removedClis.length > 0) {
            archive.mutate(
              { campaignId, clis: removedClis },
              {
                onSuccess: () => {
                  toast.success(res?.message ?? 'CLI list updated');
                  onSaved?.();
                  onOpenChange(false);
                },
                onError: () => toast.error('CLI saved, but archive step failed.'),
              },
            );
          } else {
            toast.success(res?.message ?? 'CLI list updated');
            onSaved?.();
            onOpenChange(false);
          }
        },
        onError: () => toast.error('Could not update the campaign CLIs.'),
      },
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit CLIs</DialogTitle>
            <DialogDescription>
              Update which dedicated CLIs{' '}
              <span className="font-medium text-foreground">{campaignName}</span>{' '}
              dials from.
            </DialogDescription>
          </DialogHeader>

          {fetchCurrent.isPending ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading current CLIs…
            </div>
          ) : (
            <div className="space-y-2">
              <Label>
                CLIs
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <MultiSelect
                options={cliOptions}
                value={selected}
                onChange={setSelected}
                placeholder={
                  cliPool.isLoading
                    ? 'Loading…'
                    : cliOptions.length === 0
                      ? 'No dedicated CLIs allocated'
                      : 'Pick CLIs'
                }
                searchPlaceholder="Filter CLIs…"
              />
              {removedClis.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {removedClis.length} CLI{removedClis.length === 1 ? '' : 's'} will
                  be removed — you&apos;ll be asked whether to archive them.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={edit.isPending || archive.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                edit.isPending || archive.isPending || fetchCurrent.isPending
              }
            >
              {(edit.isPending || archive.isPending) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={archiveConfirm !== null}
        onOpenChange={(o) => !o && setArchiveConfirm(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive removed CLIs?</DialogTitle>
            <DialogDescription>
              {archiveConfirm?.length ?? 0} CLI
              {archiveConfirm?.length === 1 ? ' has' : 's have'} been removed from
              this campaign. Archive them so they can&apos;t be re-allocated
              elsewhere, or keep them in the pool?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setArchiveConfirm(null)}
              disabled={edit.isPending || archive.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setArchiveConfirm(null);
                saveCli(false);
              }}
              disabled={edit.isPending || archive.isPending}
            >
              Keep in pool
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setArchiveConfirm(null);
                saveCli(true);
              }}
              disabled={edit.isPending || archive.isPending}
            >
              {(edit.isPending || archive.isPending) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
